import { styled } from '@/styles/stitches.config';
import type * as Stitches from '@stitches/react';
import { forwardRef, HTMLAttributes, PropsWithChildren } from 'react';
import { Scrollama, Step } from 'react-scrollama';

/**
 * # Scroll step trigger usage
 *
 * <ScrollRoot>
 *  <ScrollGraphic />
 *  <ScrollSteps onStepEnter={handleStepEnter}>
 *    <ScrollStep data={{id: 'step-1', data: [...]}} />
 *    <ScrollStep />
 *    <ScrollStep />
 *  </ScrollSteps>
 * </ScrollRoot>
 */

type ComposableStyledComponentProps<T, E> = PropsWithChildren<
  HTMLAttributes<E> & {
    as?: keyof JSX.IntrinsicElements;
    css?: Stitches.CSS;
  } & T
>;

export type OnStepEnterExitArgs<T> = {
  element: HTMLElement;
  data?: T;
  direction: 'up' | 'down';
  entry: IntersectionObserverEntry;
};

export type OnStepProgressArgs<T> = {
  element: HTMLElement;
  data?: T;
  progress: number;
  direction: 'up' | 'down';
  entry: IntersectionObserverEntry;
};

type ScrollStepsProps<T> = ComposableStyledComponentProps<
  {
    // Does this ScrollSteps implementation use a sticky graphic?
    hasGraphic?: boolean;
    // How far from the top of the viewport to trigger a step (as a proportion of view height)
    offset?: number | string;
    // Granularity of the progress interval in pixels (smaller = more granular)
    threshold?: number;
    // Callback that fires when the top or bottom edge of a step enters the offset threshold.
    onStepEnter?: (evt: OnStepEnterExitArgs<T>) => void;
    // Callback that fires when the top or bottom edge of a step exits the offset threshold.
    onStepExit?: (evt: OnStepEnterExitArgs<T>) => void;
    // Callback that fires the progress a step has made through the threshold.
    onStepProgress?(evt: OnStepProgressArgs<T>): void;
    // Whether to show visual debugging tools.
    debug?: boolean;
    // Whether to measure progress and trigger onStepProgress;
    progress?: boolean;
  },
  HTMLDivElement
>;

type ScrollStepProps = ComposableStyledComponentProps<
  {
    // Data to be given to <Scrollama> callbacks when step callback is triggered.
    data?: unknown;
  },
  HTMLDivElement
>;

/**
 * Wrapping component, primarily used to create a new stacking context.
 * Can be styled further using a css prop or styled composition.
 * Set the CSS variables --graphic-height and --step-height to change the
 * height of the graphic and the min-height of the steps respectively.
 */
const ScrollRoot = styled('section', {
  '--graphic-height': '100vh',
  '--step-height': '100vh',
  '--side-padding': '$space$3',

  position: 'relative',
  display: 'grid',
  gridTemplateColumns: '1fr',

  '@bp11': {
    gridTemplateColumns: '4fr 5fr',
    alignItems: 'center',
    justifyItems: 'center',
    marginBlockEnd: '50vh',
  },
});

/**
 * Container for the graph, will stick to the top of the parent.
 * Can be styled further using a css prop or styled composition.
 */
const ScrollGraphic = styled('figure', {
  position: 'sticky',
  insetBlockStart: 0,
  zIndex: 0,
  height: 'var(--graphic-height)',
  width: '100%',
  gridColumn: 1,
  margin: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',

  paddingBlockStart: 'calc(env(safe-area-inset-top))',
  paddingBlockEnd: 'calc(env(safe-area-inset-bottom))',
  paddingInlineEnd: 0,
  paddingInlineStart: 0,

  '@bp11': {
    gridColumn: 2,
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: 0,
  },
});

const ScrollStepsContainer = styled('div', {
  position: 'relative',
  /* overlay the steps on top of the graphic */
  top: 0,
  zIndex: 1,
  transform: 'translateZ(0)',
  gridColumn: 1,
  pointerEvents: 'none',

  variants: {
    hasGraphic: {
      true: {
        top: 'calc(-0.25 * var(--graphic-height))',
      },
      false: {
        top: 0,
      },
    },
  },
});

/**
 * Wrapping component for the steps. This component handles attaching an IntersectionObserver to ScrollStep.
 * Pass callbacks to this component to update your state and initiate graph transitions.
 *
 * The container here makes sure that the first step is overlaying the graphic.
 */
function ScrollSteps<T>(props: ScrollStepsProps<T>) {
  const { css, hasGraphic = true, ...rest } = props;

  return (
    <ScrollStepsContainer
      css={css}
      hasGraphic={{
        '@initial': true,
        '@bp11': hasGraphic,
      }}
    >
      <Scrollama {...rest} />
    </ScrollStepsContainer>
  );
}

const ScrollStepEl = styled('div', {
  display: 'grid',
  alignItems: 'start',
  marginBlockEnd: 'var(--step-height)',

  '@bp11': {
    marginBlockEnd: 0,
    minHeight: 'var(--step-height)',
    alignItems: 'center',
  },

  '> *': {
    backgroundColor: 'var(--color-background)',
  },
});

/**
 * A single step. Each step will trigger callbacks passed to ScrollSteps with their data prop.
 */
const ScrollStep = forwardRef<HTMLDivElement, ScrollStepProps>((props, ref) => {
  const { as = 'div', css, data, children, ...rest } = props;

  return (
    <Step data={data} ref={ref} {...rest}>
      <ScrollStepEl as={as} css={css}>
        {children}
      </ScrollStepEl>
    </Step>
  );
});

ScrollStep.displayName = 'ForwardedRefScrollStep';

export { ScrollRoot, ScrollGraphic, ScrollSteps, ScrollStep };
